/**
 * sessionSync — keep this tab's session in step with the token other tabs
 * store.
 *
 * The session token lives in localStorage.ktok, shared by every tab of the
 * browser profile, and every request reads it fresh. Nothing used to notice
 * another tab changing it: in production (2026-10-16) an admin's customer-A
 * tab kept polling after a demo login elsewhere, merged the demo account's
 * devices into its state while still on /sites/Windmills, and rendered "No
 * taps found for this site" — indistinguishable from a real empty site.
 *
 * Two triggers, one decision (syncSessionWithStorage):
 *  - the window 'storage' event for the token key (installSessionSync);
 *  - lazily, GlobalAccess.getDevices before every fetch, for tabs that missed
 *    the event (it is never delivered to the writing tab, and some browsers
 *    coalesce). It calls the handler registered here instead of fetching.
 */

import { Router } from 'preact-router';
import GlobalAccess from './globalAccess';
import { getCconfig } from './cconfigService';
import { showInfo } from './toastService';
import { getToken, isOwnTokenWrite, forgetOwnTokenWrite, TOKEN_STORAGE_KEY } from './tokenStorage';
import { resetSession, resetAccountState } from './sessionReset';
import { AppConstant } from '../constant/AppConstant';
import { ROUTE } from '../constant/utils';
import errorLogger from '../utils/errorLogger';
import { runWhenVisible } from '../utils/tabVisibility';

const LOGIN_ROUTE = '/';

// Token an adoption is in progress for, until its setState has committed.
// Between the storage event and that commit app.state.ktok still holds the
// OLD token, so a poll timer firing in that window would see "changed
// elsewhere" again and adopt a second time (second reset, second toast).
let adoptingToken = null;

/** Another tab signed out: end this tab's session too. */
function followSignOut(app) {
  adoptingToken = null;
  resetSession(app);
  Router.route(LOGIN_ROUTE);
}

/**
 * Another tab signed in (as a different account, or while this tab was
 * signed out): drop the old account's state and caches, keep the token the
 * other tab stored, and continue as that session on Sites.
 */
function adoptStoredSession(app, storedToken, t) {
  const wasSignedIn = !!app.state.ktok;
  forgetOwnTokenWrite();
  adoptingToken = storedToken;
  resetAccountState(
    app,
    // Keep the page's refresh preference: if this tab is already on Sites it
    // does not remount, and the poll loop only fetches while refresh is set.
    { ktok: storedToken, refresh: !!app.state.refresh },
    // Once committed, app.state.ktok matches the stored token, so this fetch
    // passes getDevices' session check and loads the adopted account.
    () => {
      if (adoptingToken === storedToken) adoptingToken = null;
      GlobalAccess.getDevices(app);
    },
  );
  if (typeof app.startPollingIfNeeded === 'function') app.startPollingIfNeeded();
  // A background tab has no use for the theme yet, and a /customer read per tab
  // spends the shared token's request allowance.
  runWhenVisible(() => {
    getCconfig()
      .then((cconfig) => {
        if (typeof app.applyTheme === 'function') app.applyTheme(cconfig.theme_pref || 'dark');
      })
      .catch(() => {});
  });
  Router.route(ROUTE.SITES);
  if (wasSignedIn) showInfo(t(AppConstant.SESSION_SWITCHED_ELSEWHERE));
}

/**
 * Compare the stored token with the one this tab's state was built from
 * (app.state.ktok) and follow any change another tab made.
 *
 * @param {object} app   The App instance.
 * @param {Function} t   i18next translate function.
 * @returns {boolean}    true when the session changed (callers must not use
 *                       the old state for the request they were about to make).
 */
export function syncSessionWithStorage(app, t) {
  if (!app || !app.state) return false;
  const storedToken = getToken();
  const tabToken = app.state.ktok || null;
  if (storedToken === tabToken) return false;
  // This tab wrote it (a login whose setState({ ktok }) has not committed).
  if (isOwnTokenWrite(storedToken)) return false;
  // Already adopting exactly this token; the caller still must not fetch
  // with the old state, so report "changed" without adopting again.
  if (storedToken && storedToken === adoptingToken) return true;

  if (!storedToken) {
    followSignOut(app);
  } else {
    adoptStoredSession(app, storedToken, t);
  }
  return true;
}

/**
 * Install the cross-tab listener and the lazy-detection handler. App calls it
 * at mount and the returned function at unmount.
 *
 * @param {object} app
 * @param {{ t: Function }} options
 * @returns {Function} uninstall
 */
export function installSessionSync(app, { t }) {
  const sync = () => {
    try {
      return syncSessionWithStorage(app, t);
    } catch (err) {
      errorLogger.logError('SessionSyncFailed', err);
      return true; // never fetch with a token whose state could not be reconciled
    }
  };
  // key === null: localStorage.clear() in another tab.
  const onStorage = (event) => {
    if (event.key === TOKEN_STORAGE_KEY || event.key === null) sync();
  };

  window.addEventListener('storage', onStorage);
  GlobalAccess.setSessionChangeHandler(sync);

  return () => {
    window.removeEventListener('storage', onStorage);
    GlobalAccess.setSessionChangeHandler(null);
  };
}
